import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  DatePicker,
  Alert,
  InputNumber,
  Radio,
  RadioChangeEvent,
  Select,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import axios from "axios";
import CommonSelect from "./commonSelect";
import { notify } from "../../services/toast.config";

dayjs.extend(isSameOrAfter);

interface Document {
  name: string;
  url: string;
  type: string;
  public_id?: string;
}

interface LeaveFormData {
  leaveType: string;
  fromDate: Dayjs | null;
  toDate: Dayjs | null;
  dayType: string;
  reason: string;
  partialDays: number;
  documents: Document[];
  startDayPart: string;
  endDayPart: string;
}

interface LeaveBalanceData {
  leaveTypeId: number;
  leaveTypeName: string;
  unlimited: boolean;
  isMonthlyAccrual: boolean;
  monthlyAllocation: number;
  monthlyLimit: number;
  monthlyCarryForward: boolean;
  yearlyTotal: number;
  annualBalance: number;
  currentMonthAllocated: number;
  currentMonthUsed: number;
  totalAccumulated: number;
  usedLeaves: number;
  pendingLeaves: number;
  rejectedOrCancelledLeaves: number;
  balance: number;
  remainingLeaves: number;
}

interface LeaveFormProps {
  mode: "add" | "edit";
  leaveTypes: Array<{
    value: string;
    label: string;
    isMonthlyAccrual?: boolean;
    monthlyLimit?: number;
    monthlyAllocation?: number;
    isActive?: boolean;
    balance?: number;
  }>;
  dayTypes: Array<{ value: string; label: string }>;
  initialValues?: {
    leaveType?: string | { value: string; label: string };
    fromDate?: string;
    toDate?: string;
    dayType?: string;
    reason?: string;
    partialDays?: number;
    documents?: Document[] | string[];
    startDayPart?: string;
    endDayPart?: string;
  };
  leaveBalances: LeaveBalanceData[];
  onSubmit: (values: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

// Helper to display error message text
const ErrorText = ({ message }: { message: string }) => {
  if (!message) return null;
  return <div className="text-danger mt-1 fs-12">{message}</div>;
};

const LeaveForm: React.FC<LeaveFormProps> = ({
  mode,
  leaveTypes,
  dayTypes,
  initialValues,
  leaveBalances,
  onSubmit,
  onCancel,
  loading = false,
}) => {
  const user = useSelector((state: any) => state.user.user);

  const [formValues, setFormValues] = useState<LeaveFormData>({
    leaveType:
      typeof initialValues?.leaveType === "object"
        ? initialValues.leaveType.value
        : initialValues?.leaveType || "",
    fromDate: initialValues?.fromDate
      ? dayjs(initialValues.fromDate, "DD-MM-YYYY")
      : null,
    toDate: initialValues?.toDate
      ? dayjs(initialValues.toDate, "DD-MM-YYYY")
      : null,
    dayType: initialValues?.dayType || dayTypes[0]?.value || "",
    reason: initialValues?.reason || "",
    partialDays: initialValues?.partialDays || 0,
    documents: [],
    startDayPart: initialValues?.startDayPart || "full",
    endDayPart: initialValues?.endDayPart || "full",
  });

  const [filePreviews, setFilePreviews] = useState<Document[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [isSingleDay, setIsSingleDay] = useState(true);

  // Validation State
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const [uploading, setUploading] = useState(false);
  const [dateRangeError, setDateRangeError] = useState("");

  const originalLeaveDays = useMemo(() => {
    if (mode === "edit" && initialValues) {
      if ("days" in initialValues && typeof initialValues.days === "number") {
        return initialValues.days;
      }
      if (initialValues.fromDate && initialValues.toDate) {
        const originalFrom = dayjs(initialValues.fromDate, "DD-MM-YYYY");
        const originalTo = dayjs(initialValues.toDate, "DD-MM-YYYY");
        let days = originalTo.diff(originalFrom, "day") + 1;
        const startPart = initialValues.startDayPart || "full";
        const endPart = initialValues.endDayPart || "full";
        const typeLabel = leaveTypes.find(
          (lt) => lt.value === initialValues.leaveType ||
            (typeof initialValues.leaveType === 'object' && initialValues.leaveType.value === lt.value)
        )?.label;
        if (typeLabel === "Short Leave") return 1;
        if (startPart !== "full") days -= 0.5;
        if (endPart !== "full") days -= 0.5;
        if (days === 0 && startPart === endPart && startPart !== "full") {
          days = 0.5;
        }
        return Math.max(0, days);
      }
    }
    return 0;
  }, [mode, initialValues]);

  const selectedLeaveBalance = useMemo(
    () =>
      leaveBalances.find(
        (lb) => lb.leaveTypeId === Number(formValues.leaveType)
      ),
    [leaveBalances, formValues.leaveType]
  );

  const selectedLeaveType = useMemo(
    () => leaveTypes.find((lt) => lt.value === formValues.leaveType),
    [leaveTypes, formValues.leaveType]
  );

  const isUnlimited =
    selectedLeaveBalance?.unlimited ?? selectedLeaveType?.balance === -1;

  const isShortLeave = selectedLeaveType?.label === "Short Leave";
  const isMonthlyCarryForward = selectedLeaveBalance?.monthlyCarryForward ?? false;

  const currentMonth = useMemo(() => {
    const now = dayjs();
    return {
      start: now.startOf('month'),
      end: now.endOf('month'),
      key: now.format('YYYY-MM')
    };
  }, []);

  const getDateRestrictionMessage = () => {
    if (isUnlimited) return null;

    if (isMonthlyCarryForward) {
      return "Monthly carry-forward leave: Can apply for current and future months only";
    } else {
      return "This leave can only be applied within current or past months";
    }
  };

  const remainingDays = useMemo(() => {
    const baseBalance = selectedLeaveBalance?.balance ?? 0;
    if (mode === "edit" && originalLeaveDays > 0) {
      return baseBalance + originalLeaveDays;
    }
    return baseBalance;
  }, [selectedLeaveBalance, mode, originalLeaveDays]);

  const monthlyLimit = selectedLeaveBalance?.monthlyLimit ?? null;
  const isMonthlyAccrual = selectedLeaveBalance?.isMonthlyAccrual ?? false;

  useEffect(() => {
    if (mode === "edit" && initialValues) {
      const parsedDocuments = (initialValues.documents || []).map((doc) =>
        typeof doc === "string" ? JSON.parse(doc) : doc
      );

      setFormValues((prev) => ({
        ...prev,
        documents: parsedDocuments,
        startDayPart: initialValues.startDayPart || "full",
        endDayPart: initialValues.endDayPart || "full",
      }));
      setFilePreviews(parsedDocuments);
    }
  }, [initialValues, mode]);

  // Main Calculation & Validation Logic
  useEffect(() => {
    // Clear date errors if fields are empty
    if (!formValues.fromDate || !formValues.toDate) {
      setCalculatedDays(0);
      setIsSingleDay(true);
      setDateRangeError("");
      setErrors(prev => ({ ...prev, balance: "", monthlyLimit: "" }));
      return;
    }

    const dayDifference =
      formValues.toDate.diff(formValues.fromDate, "day") + 1;
    const isSingle = dayDifference === 1;
    setIsSingleDay(isSingle);

    let days = dayDifference;
    let rangeErr = "";

    // --- Validate Short Leave ---
    if (isShortLeave) {
      days = 1;
      if (dayDifference > 1) {
        rangeErr = "Short Leave can only be applied for a single day.";
      }
    } else {
      // --- Validate Standard Leave ---
      if (formValues.startDayPart !== "full") days -= 0.5;
      if (formValues.endDayPart !== "full") days -= 0.5;

      if (days < 0) days = 0;

      // Prevent invalid Half Day selection on single day
      if (
        isSingle &&
        formValues.startDayPart === "second_half" &&
        formValues.endDayPart === "first_half"
      ) {
        rangeErr = "Invalid selection order (Start > End).";
        days = 0;
      }

      // Sync single day parts for logic
      if (isSingle && formValues.startDayPart !== "full") {
        if (
          formValues.startDayPart === "first_half" &&
          formValues.endDayPart === "first_half"
        )
          days = 0.5;
        else if (
          formValues.startDayPart === "second_half" &&
          formValues.endDayPart === "second_half"
        )
          days = 0.5;
      }
    }

    setCalculatedDays(days);
    setDateRangeError(rangeErr);

    // -- Balance & Limit Errors --
    const newErrors: Record<string, string> = {};

    if (!isUnlimited && formValues.leaveType && !rangeErr) {
      // Monthly Limit Check
      if (isMonthlyAccrual && monthlyLimit !== null && monthlyLimit > 0) {
        if (days > monthlyLimit) {
          newErrors.monthlyLimit = `Limit exceeded: Max ${monthlyLimit} days/month allowed.`;
        }
      }

      // Balance Check
      if (remainingDays < days) {
        newErrors.balance = `Insufficient balance: You have ${remainingDays} days available.`;
      }
    }

    setErrors(prev => ({ ...prev, balance: newErrors.balance, monthlyLimit: newErrors.monthlyLimit }));

  }, [
    formValues.fromDate,
    formValues.toDate,
    formValues.startDayPart,
    formValues.endDayPart,
    remainingDays,
    isMonthlyAccrual,
    monthlyLimit,
    isUnlimited,
    formValues.leaveType,
    isShortLeave,
  ]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    let isValid = true;

    if (!formValues.leaveType) {
      newErrors.leaveType = "Please select a leave type";
      isValid = false;
    }
    if (!formValues.fromDate) {
      newErrors.fromDate = "Start date is required";
      isValid = false;
    }
    if (!formValues.toDate) {
      newErrors.toDate = "End date is required";
      isValid = false;
    }
    if (!formValues.reason.trim()) {
      newErrors.reason = "Reason is required";
      isValid = false;
    }
    if (isShortLeave && formValues.startDayPart === "full") {
      newErrors.startDayPart = "Please select Short Leave timing";
      isValid = false;
    }

    // Include existing calculation errors
    if (errors.balance) isValid = false;
    if (errors.monthlyLimit) isValid = false;
    if (dateRangeError) isValid = false;

    setErrors(prev => ({ ...prev, ...newErrors }));
    setTouched({
      leaveType: true,
      fromDate: true,
      toDate: true,
      reason: true,
      startDayPart: true
    });

    return isValid;
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
  };

  const handleLeaveTypeChange = useCallback((selectedOption: any) => {
    const value = selectedOption?.value || selectedOption;
    setFormValues((prev) => ({
      ...prev,
      leaveType: value,
      partialDays: 0,
      fromDate: null,
      toDate: null,
      startDayPart: "full",
      endDayPart: "full",
    }));
    setErrors({});
    setDateRangeError("");
  }, []);

  const handleDateChange = useCallback(
    (field: "fromDate" | "toDate", date: Dayjs | null) => {
      setFormValues((prev) => {
        const updates: Partial<LeaveFormData> = {
          [field]: date,
          partialDays: 0,
          startDayPart: "full", // Reset parts on date change
          endDayPart: "full",
        };

        if (field === "fromDate") {
          if (!date) updates.toDate = null;
          else if (prev.toDate && date.isAfter(prev.toDate))
            updates.toDate = null;
          else if (
            isShortLeave ||
            (prev.toDate && prev.toDate.diff(date, "day") === 0)
          ) {
            updates.toDate = date; // Keep single day sync
          }
        }
        return { ...prev, ...updates };
      });
      // Clear specific error when changed
      setErrors(prev => ({ ...prev, [field]: "" }));
    },
    [isShortLeave]
  );

  const handleDayPartChange = (
    field: "startDayPart" | "endDayPart",
    value: string
  ) => {
    setFormValues((prev) => {
      const updates: any = { [field]: value };
      const isSingle =
        prev.fromDate && prev.toDate && prev.fromDate.isSame(prev.toDate, "day");

      if (isSingle || isShortLeave) {
        updates.startDayPart = value;
        updates.endDayPart = value;
      }
      return { ...prev, ...updates };
    });
    setErrors(prev => ({ ...prev, startDayPart: "" })); // Clear day part error
  };

  const uploadFileToCloudinary = async (file: File): Promise<any | null> => {
    const UPLOAD_PRESET: any = process.env.REACT_APP_UPLOAD_PRESET;
    const CLOUDINARY_URL = `${process.env.REACT_APP_API_CLOUDNIRY_URL}/${process.env.REACT_APP_CLOUD_NAME}/upload`;
    const FOLDER_PATH = "cms/leaves/" + user?.userDetails.id;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", UPLOAD_PRESET);
    formData.append("folder", FOLDER_PATH);

    try {
      const response = await axios.post(CLOUDINARY_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      notify.error("Upload failed.");
      return null;
    }
  };

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!e.target.files || e.target.files.length === 0) return;
      const files = Array.from(e.target.files);
      const validFiles = files.filter(
        (file) =>
          (file.type.startsWith("image/") ||
            file.type.startsWith("application/") ||
            file.type.startsWith("text/")) &&
          file.size <= 10 * 1024 * 1024
      );

      if (validFiles.length !== files.length) notify.warn("Some files skipped.");
      if (validFiles.length === 0) return;

      const newPreviews = validFiles.map((file) => ({
        name: file.name,
        url: URL.createObjectURL(file),
        type: file.type,
        file,
      }));

      setFilePreviews((prev) => [...prev, ...newPreviews]);
      setSelectedFiles((prev) => [...prev, ...validFiles]);
    },
    []
  );

  const removeDocument = useCallback((index: number) => {
    setFilePreviews((prev) => {
      const newPreviews = [...prev];
      if (newPreviews[index] && !newPreviews[index].public_id) {
        URL.revokeObjectURL(newPreviews[index].url);
      }
      const removedDoc = newPreviews.splice(index, 1)[0];
      setFormValues((current) => ({
        ...current,
        documents: current.documents.filter(
          (doc) => doc.public_id !== removedDoc.public_id
        ),
      }));
      return newPreviews;
    });
    setSelectedFiles((prev) => {
      const newFiles = [...prev];
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setUploading(true);
    try {
      const uploadPromises = selectedFiles.map((file) =>
        uploadFileToCloudinary(file)
      );
      const results = await Promise.all(uploadPromises);
      const successfulUploads = results.filter((r) => r !== null);

      const newDocuments = successfulUploads.map((upload) => ({
        name: upload.original_filename,
        url: upload.secure_url,
        type: "application/octet-stream",
        public_id: upload.public_id,
      }));

      const allDocuments = [...formValues.documents, ...newDocuments];

      const payload = {
        ...formValues,
        fromDate: formValues.fromDate?.format("DD-MM-YYYY"),
        toDate: formValues.toDate?.format("DD-MM-YYYY"),
        days: calculatedDays,
        startDayPart: formValues.startDayPart,
        endDayPart: formValues.endDayPart,
        documents: allDocuments,
      };

      await onSubmit(payload);
      if (mode === "add") {
        setFormValues({
          leaveType: "",
          fromDate: null,
          toDate: null,
          dayType: "",
          reason: "",
          partialDays: 0,
          documents: [],
          startDayPart: "full",
          endDayPart: "full",
        });
        setFilePreviews([]);
        setSelectedFiles([]);
        setErrors({});
        setTouched({});
      }
    } catch (error) {
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* Global Error Alert for Critical Issues */}
      {(errors.balance || errors.monthlyLimit || dateRangeError) && (
        <div className="mb-3">
          <Alert
            message="Cannot Apply Leave"
            description={errors.balance || errors.monthlyLimit || dateRangeError}
            type="error"
            showIcon
          />
        </div>
      )}

      <div className="row">
        {mode === "edit" && (
          <div className="col-md-12">
            <div className="alert alert-info">
              Editing Leave Request - Original {originalLeaveDays} days will be
              restored.
            </div>
          </div>
        )}

        <div className="col-md-12 mb-3">
          <label className="form-label">
            Leave Type <span className="text-danger">*</span>
          </label>
          <div className={touched.leaveType && errors.leaveType ? "is-invalid" : ""}>
            <CommonSelect
              options={leaveTypes}
              defaultValue={leaveTypes.find(
                (lt) => lt.value === formValues.leaveType
              )}
              onChange={handleLeaveTypeChange}
              isDisabled={mode === "edit"}
            />
          </div>
          {touched.leaveType && <ErrorText message={errors.leaveType} />}
        </div>

        {/* Date Selection */}
        <div className="col-md-6 mb-3">
          <label className="form-label">From Date <span className="text-danger">*</span></label>
          <DatePicker
            status={touched.fromDate && errors.fromDate ? "error" : ""}
            format="DD-MM-YYYY"
            value={formValues.fromDate}
            onChange={(date) => handleDateChange("fromDate", date)}
            onBlur={() => handleBlur("fromDate")}
            style={{ width: "100%" }}
            disabledDate={(current) => {
              if (!current || !formValues.leaveType) return false;
              return false;
            }}
          />
          {touched.fromDate && <ErrorText message={errors.fromDate} />}
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">To Date <span className="text-danger">*</span></label>
          <DatePicker
            status={touched.toDate && errors.toDate ? "error" : ""}
            format="DD-MM-YYYY"
            value={formValues.toDate}
            onChange={(date) => handleDateChange("toDate", date)}
            onBlur={() => handleBlur("toDate")}
            style={{ width: "100%" }}
            disabledDate={(current) => {
              if (!current || !formValues.leaveType) return false;

              if (isShortLeave) {
                if (!formValues.fromDate) return true;
                return !current.isSame(formValues.fromDate, "day");
              }
              if (!formValues.fromDate) return false;
              return current.isBefore(formValues.fromDate, "day");
            }}
          />
          {touched.toDate && <ErrorText message={errors.toDate} />}
        </div>

        {/* --- DYNAMIC DAY PART SECTION --- */}
        {formValues.fromDate && formValues.toDate && (
          <div className="col-md-12 mb-3">
            <div className={`p-3 bg-light rounded border ${errors.startDayPart ? "border-danger" : ""}`}>
              <label className="form-label fw-bold mb-2">
                Leave Duration: {calculatedDays} Day(s)
              </label>

              {isShortLeave ? (
                /* Short Leave UI */
                <div>
                  <label className="form-label small text-muted">
                    Select Timing <span className="text-danger">*</span>
                  </label>
                  <div>
                    <Radio.Group
                      value={formValues.startDayPart}
                      onChange={(e) =>
                        handleDayPartChange("startDayPart", e.target.value)
                      }
                      buttonStyle="solid"
                    >
                      <Radio.Button value="first_half">
                        First Half (9:30 AM - 11:30 AM)
                      </Radio.Button>
                      <Radio.Button value="second_half">
                        Second Half (4 PM - 6 PM)
                      </Radio.Button>
                    </Radio.Group>
                  </div>
                  {errors.startDayPart && <ErrorText message={errors.startDayPart} />}
                </div>
              ) : isSingleDay ? (
                /* Single Day Standard UI */
                <div>
                  <label className="form-label small text-muted">
                    Select Day Type
                  </label>
                  <Radio.Group
                    value={formValues.startDayPart}
                    onChange={(e) =>
                      handleDayPartChange("startDayPart", e.target.value)
                    }
                    buttonStyle="solid"
                  >
                    <Radio.Button value="full">Full Day</Radio.Button>
                    <Radio.Button value="first_half">
                      First Half Only
                    </Radio.Button>
                    <Radio.Button value="second_half">
                      Second Half Only
                    </Radio.Button>
                  </Radio.Group>
                </div>
              ) : (
                /* Multi Day UI */
                <div className="row">
                  <div className="col-md-6">
                    <label className="form-label small text-muted">
                      Start Date ({formValues.fromDate.format("DD MMM")})
                    </label>
                    <Select
                      value={formValues.startDayPart}
                      onChange={(val) => handleDayPartChange("startDayPart", val)}
                      options={[
                        { value: "full", label: "Full Day" },
                        { value: "second_half", label: "Half Day (Afternoon)" },
                      ]}
                      style={{ width: "100%" }}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      dropdownStyle={{ zIndex: 2000 }}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small text-muted">
                      End Date ({formValues.toDate.format("DD MMM")})
                    </label>
                    <Select
                      value={formValues.endDayPart}
                      onChange={(val) => handleDayPartChange("endDayPart", val)}
                      options={[
                        { value: "full", label: "Full Day" },
                        { value: "first_half", label: "Half Day (Morning)" },
                      ]}
                      style={{ width: "100%" }}
                      getPopupContainer={(trigger) => trigger.parentElement}
                      dropdownStyle={{ zIndex: 2000 }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="col-md-12 mb-3">
          <label className="form-label">Reason <span className="text-danger">*</span></label>
          <textarea
            className={`form-control ${touched.reason && errors.reason ? "is-invalid" : ""}`}
            rows={3}
            value={formValues.reason}
            onChange={(e) => {
              setFormValues({ ...formValues, reason: e.target.value });
              if (e.target.value.trim()) setErrors(prev => ({ ...prev, reason: "" }));
            }}
            onBlur={() => handleBlur("reason")}
          />
          {touched.reason && <ErrorText message={errors.reason} />}
        </div>

        {/* Upload Section */}
        <div className="col-md-12 mb-3">
          <label className="form-label">Upload Documents</label>
          <div className="bg-light rounded p-2">
            <input type="file" multiple onChange={handleFileChange} />
            <div className="mt-2">
              {filePreviews.map((f, i) => (
                <div key={i} className="d-flex justify-content-between p-1">
                  <span>{f.name}</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-danger"
                    onClick={() => removeDocument(i)}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="modal-footer">
        <button
          type="button"
          className="btn btn-light me-2"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={
            loading ||
            uploading ||
            !!dateRangeError ||
            Object.values(errors).some((err) => !!err)
          }
        >
          {uploading ? "Uploading..." : mode === "add" ? "Apply" : "Save"}
        </button>
      </div>
    </form>
  );
};

export default LeaveForm;
